import {fabric} from 'fabric';

export enum FileType {
  PNG = 'PNG',
  SVG = 'SVG',
}

export async function DownloadTower(canvas: fabric.Canvas, fileType: FileType): Promise<void> {
  if(!canvas) return;

  // Convert current canvas to a json string
  const cJSON = canvas.toJSON() || {};
  const currTower = JSON.stringify(cJSON);

  // Create temporary canvas to crop image
  const tempCanvas = new fabric.Canvas(null, {});

  tempCanvas.loadFromJSON(currTower, async () => {

    tempCanvas.setZoom(1);
    tempCanvas.absolutePan({
      x: 0,
      y: 0,
    });

    // Get the bounding box of all the objects on the tempCanvas
    type BoundingBox = {minX: number, minY: number, maxX: number, maxY: number}
    const boundingBox = tempCanvas.getObjects().reduce((acc: BoundingBox, obj) => {
      const objCoords = obj.getBoundingRect();
      return {
        minX: Math.min(acc.minX, objCoords.left),
        minY: Math.min(acc.minY, objCoords.top),
        maxX: Math.max(acc.maxX, objCoords.left + objCoords.width),
        maxY: Math.max(acc.maxY, objCoords.top + objCoords.height),
      };
    }, {
      minX: Number.MAX_VALUE,
      minY: Number.MAX_VALUE,
      maxX: Number.MIN_VALUE,
      maxY: Number.MIN_VALUE,
    }) as BoundingBox;

    const bboxWidth = boundingBox.maxX - boundingBox.minX;
    const bboxHeight = boundingBox.maxY - boundingBox.minY;

    // Set the top and left starting points for the canvas
    tempCanvas.absolutePan({
      x: boundingBox.minX,
      y: boundingBox.minY,
    });

    // Set the tempCanvas dimensions to match the bounding box dimensions
    tempCanvas.setDimensions({
      width: bboxWidth,
      height: bboxHeight,
    });

    switch (fileType) {
      case FileType.SVG:
        DownloadAsSVG(tempCanvas.toSVG() ?? '', 'program_towers.svg');
        break;
      case FileType.PNG:
        DownloadFromURL(await PngFromSvg(tempCanvas.toSVG() ?? ''), 'program_towers.png')
        break;
    }

    tempCanvas.clear();
    tempCanvas.dispose();

  });
}

export function DownloadAsSVG(svg: string, filename: string): void {
  if (!svg) {
    console.error('no SVG string provided');
    return;
  }

  if (!filename) {
    console.error('no filename provided for SVG');
    return;
  }

  const link = document.createElement('a');
  link.download = filename;
  link.href = 'data:image/svg+xml;utf8,' + encodeURIComponent(svg);
  link.click();
}

function DownloadFromURL(url: string, filename: string): void {
  if (!url) {
    console.error('no url provided');
    return;
  }

  if (!filename) {
    console.error('no filename provided');
    return;
  }

  const link = document.createElement('a');
  link.download = filename;
  link.href = url;
  link.click();
}

async function PngFromSvg(svg: string): Promise<string> {
  try {
    const svgBlob = new Blob([svg], {type: 'image/svg+xml'});

    const img = new Image();
    img.src = URL.createObjectURL(svgBlob);

    await new Promise(resolve => {
      img.onload = resolve;
    });

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = img.width;
    canvas.height = img.height;

    context?.drawImage(img, 0, 0);

    const pngBlob = await new Promise((resolve: BlobCallback) => {
      canvas.toBlob(resolve, 'image/png');
    });

    if(pngBlob){
      return URL.createObjectURL(pngBlob);
    }
  } catch (error) {
    console.error('Error converting SVG blob to PNG:', error);
  }

  return '';
}
